import { Tag, Timestamp } from '@dixa/lib-shared-habla';
import capitalize from 'lodash/capitalize';
import { ReactNode, useContext } from 'react';

import { UserAuditLogEntry } from 'apis/auditLogs';
import { conversationStatusAttributes } from 'utils/conversation';

import { UserLink } from '../activityTypes/userLink/userLink';

import { ConversationLink } from './conversationLink/conversationLink';
import { ConversationPreviewButton } from './conversationLink/conversationPreviewButton';
import styles from './conversationStarted.module.scss';
import { SummaryItem, SummaryItems } from './summaryItems';
import { CardField, UserActivityCard } from './userActivityCard';
import { UserActivityLayoutContext } from './userActivityLayoutContext';
import { channelRefIcon, statusColors } from './userActivityTypes.utils';

type ConversationStartedProps = {
  entry: UserAuditLogEntry<'CONVERSATION_CREATED'>;
};

export const ConversationStarted = ({
  entry,
}: ConversationStartedProps): JSX.Element => {
  const status = entry.conversation_state
    ? conversationStatusAttributes[entry.conversation_state]
    : null;
  const hasFollowUp =
    entry.conversation_state === 'pending' ||
    entry.conversation_state === 'awaitingpending';
  // The sidebar only shows the status and subject up front, with the other details revealed on expand.
  const isCompact = useContext(UserActivityLayoutContext) === 'compact';

  const statusItem: SummaryItem = {
    key: 'status',
    content: status && (
      <Tag className={styles.statusTag} color={status.color} noBorder>
        {status.label}
      </Tag>
    ),
    label: 'Status',
  };
  const subjectItem: SummaryItem = {
    key: 'subject',
    content: entry.subject,
    label: 'Subject',
  };

  const lastMessage = entry.last_message_created_at && (
    <Timestamp relative time={entry.last_message_created_at} />
  );
  const followUpExpiry =
    hasFollowUp && entry.pending_expiration_at ? (
      <Timestamp relative time={entry.pending_expiration_at} />
    ) : undefined;

  const details: readonly {
    readonly key: string;
    readonly label: string;
    readonly value: ReactNode;
    /** What the summary row shows, when it differs from `value`. */
    readonly summary?: ReactNode;
  }[] = [
    { key: 'queue', label: 'Queue', value: entry.queue_name },
    {
      key: 'assignee',
      label: 'Assignee',
      value: entry.assigned_agent_id ? (
        <UserLink
          userId={entry.assigned_agent_id}
          onClick={(event) => event.stopPropagation()}
        >
          {entry.assigned_agent_name ?? entry.assigned_agent_id}
        </UserLink>
      ) : (
        // Only when the conversation's details were resolved, so a missing lookup isn't shown as unassigned.
        status && 'Unassigned'
      ),
    },
    {
      key: 'direction',
      label: 'Direction',
      value: entry.direction && capitalize(entry.direction),
    },
    {
      key: 'last-message',
      label: 'Last message',
      value: lastMessage,
      summary: lastMessage && <span>Last message {lastMessage}</span>,
    },
    {
      key: 'follow-up',
      label: 'Follow-up expires',
      value: followUpExpiry,
      summary: followUpExpiry && (
        <span>Follow-up expires {followUpExpiry}</span>
      ),
    },
  ];

  const summaryItems: SummaryItem[] = isCompact
    ? [statusItem, subjectItem]
    : [
        statusItem,
        subjectItem,
        ...details.map(({ key, label, value, summary }) => ({
          key,
          content: summary ?? value,
          // The prefixed summaries already say what they are.
          label: summary ? undefined : label,
        })),
      ];

  const fields: CardField[] | undefined = isCompact
    ? details.map(({ label, value }) => ({ label, value }))
    : undefined;

  return (
    <UserActivityCard
      entry={entry}
      icon={channelRefIcon(entry.channel_ref)}
      color={statusColors.info}
      title="Conversation"
      titleSuffix={
        <>
          <ConversationLink
            conversationId={entry.conversation_id}
            onClick={(event) => event.stopPropagation()}
          >
            #{entry.conversation_id}
          </ConversationLink>
          <ConversationPreviewButton conversationId={entry.conversation_id} />
        </>
      }
      summary={<SummaryItems items={summaryItems} />}
      fields={fields}
    />
  );
};
